 
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Check, MapPin, Building, TreePine, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';
import { submitTestimonial, getPublicTestimonials } from '../services/api';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';

const ratingOptions = [1, 2, 3, 4, 5];

const About = () => {
  const [isVisible] = useState(true);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({
    name_or_company: '',
    region: '',
    rating: 5,
    speed: 5,
    quality: 5,
    communication: 5,
    responsiveness: 5,
    text: ''
  });

  const stats = t('about.stats', []);
  const subtitle = t('about.subtitle', 'Pieredze mežsaimniecībā');

  const fetchTestimonials = useCallback(async () => {
    try {
      const data = await getPublicTestimonials();
      setTestimonials(data || []);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    } finally {
      setTestimonialsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    setIsSubmitting(true);

    try {
      await submitTestimonial({
        ...formData,
        rating: Number(formData.rating),
        speed: Number(formData.speed),
        quality: Number(formData.quality),
        communication: Number(formData.communication),
        responsiveness: Number(formData.responsiveness)
      });
      toast.success(t('about.form.success', 'Atsauksme nosūtīta apstiprināšanai'));
      setShowForm(false);
      setFormData({
        name_or_company: '',
        region: '',
        rating: 5,
        speed: 5,
        quality: 5,
        communication: 5,
        responsiveness: 5,
        text: ''
      });
      fetchTestimonials();
    } catch (error) {
      setFormError(t('about.form.error', 'Neizdevās nosūtīt atsauksmi. Lūdzu, mēģiniet vēlreiz.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (value) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={`star-${index}`}
        className={`w-4 h-4 ${index < value ? 'text-yellow-400 fill-yellow-400' : 'text-white/20'}`}
      />
    ));
  };

  return (
    <section id="par-mums" className="py-24 section-river relative overflow-hidden" ref={sectionRef} data-testid="about-section">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-[#f8faf8] to-transparent" />
      <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#1F3D2B]/8 rounded-full blur-3xl" />
      <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#1F3D2B]/6 rounded-full blur-3xl" />
      <div className="absolute inset-0 forest-grain opacity-35" />
      <div className="absolute inset-0 forest-pattern opacity-20" />
      <div className="absolute top-24 left-1/2 w-44 h-2 forest-branch animate-sway" style={{ animationDelay: '1.2s' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column */}
          <div className={`space-y-6 transition-all duration-700 text-center lg:text-left ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.1s' }}>
            <span className="inline-block text-[#1F3D2B] font-medium text-sm tracking-wider uppercase mb-2">
              {t('about.title', 'Par uzņēmumu')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2E2E2E] leading-tight">
              {t('about.title', 'Par uzņēmumu')}
            </h2>
            <p className="text-[#2E2E2E]/70 text-lg leading-relaxed max-w-2xl mx-auto lg:mx-0">
              {t('about.description', '')}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-6 justify-items-center lg:justify-items-start">
              {stats.map((stat, index) => (
                <div key={`${stat.value}-${index}`} className="text-center lg:text-left p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300">
                  <div className="text-2xl font-bold text-[#1F3D2B] mb-1">{stat.value}</div>
                  <div className="text-sm text-[#2E2E2E]/60">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Regions */}
            <div className="space-y-3">
              <div className="flex items-center justify-center lg:justify-start gap-2 text-[#1F3D2B] font-semibold">
                <MapPin className="w-5 h-5" />
                {t('about.regionsLabel', 'Darbības reģioni')}
              </div>
              <div className="flex flex-wrap justify-center lg:justify-start gap-2">
                {(t('about.regions', [])).map((region) => (
                  <span
                    key={region}
                    className="text-xs bg-[#1F3D2B]/10 text-[#1F3D2B] px-3 py-1 rounded-full hover:bg-[#1F3D2B] hover:text-white transition-colors duration-300 cursor-default"
                  >
                    {region}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className={`lg:pl-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.2s' }}>
            <span className="inline-block text-[#1F3D2B] font-medium text-sm tracking-wider uppercase mb-4 px-4 py-1 bg-[#1F3D2B]/10 rounded-full">
              {subtitle}
            </span>
            <h3 className="text-2xl font-bold text-[#2E2E2E] mb-4">
              {t('about.title', 'Par uzņēmumu')}
            </h3>
            <p className="text-[#2E2E2E]/70 mb-6">
              {t('about.description', '')}
            </p>

            <ul className="space-y-4 mb-8">
              {(t('about.points', [])).map((point) => (
                <li 
                  key={point} 
                  className="flex items-start gap-3 group hover:translate-x-2 transition-transform duration-300"
                >
                  <div className="w-6 h-6 rounded-full bg-[#1F3D2B] flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-300">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-[#2E2E2E]/70">{point}</span>
                </li>
              ))}
            </ul>

            <div className="forest-panel rounded-xl p-6 mt-12 border border-gray-100 hover:shadow-lg transition-shadow duration-300 relative overflow-hidden" data-testid="about-sustainability-panel">
              <img
                src={companyInfo.logo.transparent}
                alt={companyInfo.name}
                className="absolute -right-10 -bottom-14 w-56 opacity-10 pointer-events-none select-none"
                data-testid="about-logo-watermark"
              />
              <div className="flex items-center gap-3 mb-3">
                <TreePine className="w-6 h-6 text-[#1F3D2B]" />
                <div className="font-semibold text-[#2E2E2E]">
                  {t('about.sustainabilityTitle', 'Ilgtspējīga meža apsaimniekošana')}
                </div>
              </div>
              <p className="text-[#2E2E2E]/60 text-sm">
                {t('about.sustainabilityText', '')}
              </p>
            </div>

            <Button
              className="inline-flex items-center justify-center px-6 py-3 forest-cta font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 group"
              asChild
              data-testid="about-contact-cta"
            >
              <a href="#kontakti">
                {t('why.ctaButton', 'Sazinieties ar mums')}
                <span className="ml-2 group-hover:translate-x-1 transition-transform duration-300">→</span>
              </a>
            </Button>
          </div>
        </div>

        {/* Testimonials Section */}
        <div className={`mt-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.3s' }} data-testid="testimonials-section">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-10">
            <div>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F3D2B] uppercase tracking-wide">
                <Building className="w-4 h-4" />
                {t('about.testimonialsEyebrow', 'Atsauksmes')}
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold text-[#2E2E2E] mt-2">
                {t('about.testimonialsTitle', 'Ko saka mūsu klienti')}
              </h3>
              <p className="text-[#2E2E2E]/70 mt-3 max-w-2xl">
                {t('about.testimonialsDescription', '')}
              </p>
            </div>
            <Button
              className="forest-cta px-6 py-3 text-base"
              onClick={() => setShowForm(true)}
              data-testid="testimonials-add-button"
            >
              {t('about.testimonialsAdd', 'Pievienot atsauksmi')}
            </Button>
          </div>

          {testimonialsLoading ? (
            <div className="text-[#2E2E2E]/60">{t('about.testimonialsLoading', 'Ielādē atsauksmes...')}</div>
          ) : testimonials.length === 0 ? (
            <div className="forest-panel rounded-xl p-6 text-[#2E2E2E]/60" data-testid="testimonials-empty">
              {t('about.testimonialsEmpty', '')}
            </div>
          ) : (
            <Carousel
              className="relative"
              opts={{ align: 'start', loop: testimonials.length > 3 }}
              data-testid="testimonials-carousel"
            >
              <CarouselContent>
                {testimonials.map((testimonial) => {
                  const average = Math.round(
                    (testimonial.rating + testimonial.speed + testimonial.quality + testimonial.communication + testimonial.responsiveness) / 5
                  );
                  return (
                    <CarouselItem
                      key={testimonial.id}
                      className="md:basis-1/2 lg:basis-1/3"
                      data-testid={`testimonials-slide-${testimonial.id}`}
                    >
                      <div className="forest-panel rounded-2xl p-6 card-shine hover-lift h-full">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-lg font-semibold text-[#1F3D2B]">
                              {testimonial.name_or_company}
                            </div>
                            <div className="text-sm text-[#2E2E2E]/60">{testimonial.region}</div>
                          </div>
                          <div className="flex items-center gap-1">
                            {renderStars(average)}
                          </div>
                        </div>
                        <p className="text-[#2E2E2E]/70 text-sm mt-4">{testimonial.text}</p>
                        <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-[#2E2E2E]/60">
                          <div>{t('about.metrics.speed', 'Ātrums')}: {testimonial.speed}/5</div>
                          <div>{t('about.metrics.quality', 'Kvalitāte')}: {testimonial.quality}/5</div>
                          <div>{t('about.metrics.communication', 'Komunikācija')}: {testimonial.communication}/5</div>
                          <div>{t('about.metrics.responsiveness', 'Atsaucība')}: {testimonial.responsiveness}/5</div>
                        </div>
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <CarouselPrevious className="-left-6" data-testid="testimonials-carousel-prev" />
              <CarouselNext className="-right-6" data-testid="testimonials-carousel-next" />
            </Carousel>
          )}
        </div>
      </div>

      {/* Testimonial Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="testimonials-modal">
          <div className="forest-panel rounded-2xl p-6 w-full max-w-2xl">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-xl font-semibold text-[#1F3D2B]">{t('about.testimonialsModalTitle', 'Pievienot atsauksmi')}</h4>
              <button
                className="text-[#2E2E2E]/60 hover:text-[#1F3D2B]"
                onClick={() => setShowForm(false)}
                data-testid="testimonials-close-button"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#2E2E2E]">{t('about.form.nameLabel', 'Vārds vai uzņēmums')}</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-4 py-2 border border-[#1F3D2B]/20 rounded-lg"
                    value={formData.name_or_company}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name_or_company: e.target.value }))}
                    required
                    data-testid="testimonial-name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2E2E2E]">{t('about.form.regionLabel', 'Reģions')}</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-4 py-2 border border-[#1F3D2B]/20 rounded-lg"
                    value={formData.region}
                    onChange={(e) => setFormData((prev) => ({ ...prev, region: e.target.value }))}
                    required
                    data-testid="testimonial-region"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { label: t('about.form.ratingLabel', 'Vērtējums'), field: 'rating' },
                  { label: t('about.form.speedLabel', 'Ātrums'), field: 'speed' },
                  { label: t('about.form.qualityLabel', 'Kvalitāte'), field: 'quality' },
                  { label: t('about.form.communicationLabel', 'Komunikācija'), field: 'communication' },
                  { label: t('about.form.responsivenessLabel', 'Atsaucība'), field: 'responsiveness' }
                ].map((item) => (
                  <div key={item.field}>
                    <label className="text-sm font-medium text-[#2E2E2E]">{item.label}</label>
                    <select
                      className="w-full mt-1 px-3 py-2 border border-[#1F3D2B]/20 rounded-lg"
                      value={formData[item.field]}
                      onChange={(e) => setFormData((prev) => ({ ...prev, [item.field]: e.target.value }))}
                      data-testid={`testimonial-${item.field}`}
                    >
                      {ratingOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              <div>
                <label className="text-sm font-medium text-[#2E2E2E]">{t('about.form.textLabel', 'Atsauksmes teksts')}</label>
                <textarea
                  className="w-full mt-1 px-4 py-2 border border-[#1F3D2B]/20 rounded-lg h-28"
                  value={formData.text}
                  onChange={(e) => setFormData((prev) => ({ ...prev, text: e.target.value }))}
                  required
                  data-testid="testimonial-text"
                />
              </div>

              {formError && (
                <div className="text-sm text-red-600" data-testid="testimonial-error">
                  {formError}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="forest-cta-outline"
                  onClick={() => setShowForm(false)}
                >
                  {t('about.form.cancel', 'Atcelt')}
                </Button>
                <Button
                  type="submit"
                  className="forest-cta"
                  disabled={isSubmitting}
                  data-testid="testimonial-submit"
                >
                  {isSubmitting ? t('about.form.sending', 'Nosūta...') : t('about.form.submit', 'Nosūtīt atsauksmi')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default About;
